import { cn } from '@/lib/utils'
import type { BreakdownTone, InfoChip } from './types'

export interface InfoChipStackProps {
  chips: InfoChip[]
  ctaCopy?: string
  label?: string
  className?: string
}

const toneClasses: Record<BreakdownTone, string> = {
  critical: 'border-destructive/50 bg-destructive/10 text-destructive',
  high: 'border-orange-500/50 bg-orange-500/10 text-orange-700 dark:text-orange-300',
  medium: 'border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  low: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  neutral: 'border-border bg-muted/40 text-muted-foreground',
}

const toneLabels: Record<BreakdownTone, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  neutral: 'Neutral',
}

export function InfoChipStack({
  chips,
  ctaCopy,
  label = 'Information chips',
  className,
}: InfoChipStackProps) {
  return (
    <div className={cn('space-y-3', className)} aria-label={label}>
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => {
          const tone = chip.tone ?? 'neutral'
          return (
            <span
              key={`${chip.label}-${chip.detail ?? ''}`}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium',
                toneClasses[tone],
              )}
            >
              {chip.label}
              {chip.detail ? ` — ${chip.detail}` : ''}{' '}
              <span className="sr-only">Tone: {toneLabels[tone]}</span>
            </span>
          )
        })}
      </div>
      {ctaCopy ? (
        <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
          {ctaCopy}
        </p>
      ) : null}
    </div>
  )
}
